// ============================================================================
// ClientHealthBadge.tsx
//
// Santé du navigateur de la régie, à gauche du badge « Public ». Deux mesures
// passives, sans coût mesurable :
//  - retard de boucle : un minuteur de 1 s qui arrive en retard dit que le fil
//    principal est saturé (CPU) ; on garde le pire des 10 dernières secondes ;
//  - mémoire JS : performance.memory (Chrome seulement) rapportée à sa limite.
// Ni longtask ni stats WebRTC pour l'instant : affichage minimal.
//
// Le badge porte son propre état et ne re-rend que si son texte change : rien
// ne remonte dans le contexte de la régie.
// ============================================================================

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Activity } from 'lucide-react';

const TICK_MS    = 1000;
const WINDOW     = 10;
const LAG_ORANGE = 100;
const LAG_RED    = 300;
const MEM_ORANGE = 0.6;
const MEM_RED    = 0.8;

type Level = 0 | 1 | 2;
type Health = { level: Level; lagMs: number; heapMb: number | null; heapPct: number | null };

const STYLE: Record<Level, string> = {
  0: 'bg-slate-900/80 text-emerald-400 border-slate-700',
  1: 'bg-amber-500 text-slate-900 border-amber-300',
  2: 'bg-red-600 text-white border-red-400 animate-pulse',
};
const LABEL: Record<Level, string> = { 0: 'OK', 1: 'Chargé', 2: 'Saturé' };

const readHeap = (): { mb: number; pct: number } | null => {
  const m = (performance as any).memory;
  if (!m || !m.jsHeapSizeLimit) return null;
  return { mb: Math.round(m.usedJSHeapSize / 1048576), pct: m.usedJSHeapSize / m.jsHeapSizeLimit };
};

const ClientHealthBadge: React.FC = () => {
  const [slot,   setSlot]   = useState<HTMLElement | null>(null);
  const [health, setHealth] = useState<Health>({ level: 0, lagMs: 0, heapMb: null, heapPct: null });

  useEffect(() => {
    const find = () => setSlot(document.getElementById('stage-dock-slot'));
    find();
    const observer = new MutationObserver(find);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const lags: number[] = [];
    let last = performance.now();
    const timer = setInterval(() => {
      const now = performance.now();
      const lag = Math.max(0, now - last - TICK_MS);
      last = now;
      // Onglet masqué : Chrome étire les minuteurs, le retard ne dit plus rien.
      if (document.hidden) { lags.length = 0; return; }
      lags.push(lag);
      if (lags.length > WINDOW) lags.shift();
      const worst = Math.round(Math.max(...lags));
      const heap  = readHeap();
      const lagLevel: Level = worst >= LAG_RED ? 2 : worst >= LAG_ORANGE ? 1 : 0;
      const memLevel: Level = !heap ? 0 : heap.pct >= MEM_RED ? 2 : heap.pct >= MEM_ORANGE ? 1 : 0;
      const next: Health = {
        level:   Math.max(lagLevel, memLevel) as Level,
        lagMs:   worst,
        heapMb:  heap ? heap.mb : null,
        heapPct: heap ? Math.round(heap.pct * 100) : null,
      };
      setHealth(prev => (prev.level === next.level && prev.lagMs === next.lagMs && prev.heapMb === next.heapMb ? prev : next));
    }, TICK_MS);
    return () => clearInterval(timer);
  }, []);

  if (!slot) return null;

  const title = `Santé du navigateur — retard de boucle (pire des ${WINDOW} dernières s) : ${health.lagMs} ms`
    + (health.heapMb !== null ? ` · mémoire JS : ${health.heapMb} Mo (${health.heapPct} % de la limite)` : ' · mémoire JS : non mesurable sur ce navigateur');

  return createPortal(
    <span
      title={title}
      style={{ order: -1, marginRight: 8 }}
      className={`flex items-center gap-2 whitespace-nowrap rounded-full border px-3 py-1 text-sm font-semibold shadow-lg transition-colors ${STYLE[health.level]}`}
    >
      <Activity className="w-4 h-4" />
      {LABEL[health.level]}
      <span className="text-xs opacity-80">{health.lagMs} ms{health.heapMb !== null ? ` · ${health.heapMb} Mo` : ''}</span>
    </span>,
    slot,
  );
};

export default ClientHealthBadge;
